import React, { useState, useRef, useCallback } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { FaRegDotCircle } from 'react-icons/fa';

import getValidationErrors from '../../utils/getValidationErrors';

import Background from '../../components/Background';
import Header from '../../components/Header';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';

import { Wrapper, Container, Content } from './styles';

interface ContactFormData {
  name: string;
  email: string;
  company: string;
  description: string;
}

const Contact: React.FC = () => {
  const [service, setService] = useState('AppDesign');

  const formRef = useRef<FormHandles>(null);

  const handleEncode = useCallback(data => {
    return Object.keys(data)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
      .join('&');
  }, []);

  const handleSubmit = useCallback(
    async (data: ContactFormData) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required(),
          email: Yup.string().email().required(),
          company: Yup.string().required(),
          description: Yup.string().required(),
        });

        await schema.validate(data, { abortEarly: false });

        const contactData = { ...data, service };

        await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: handleEncode({ 'form-name': 'contact', ...contactData }),
        });

        toast('Next step is coming!', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        formRef.current?.reset();
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }

        toast.error('⛔️ Something went wrong! try again.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    },
    [handleEncode, service],
  );

  return (
    <Wrapper>
      <Container>
        <Background />
        <Header />

        <Content>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <legend>
              <h1>
                Let's create
                <br />
                Progress together
              </h1>
            </legend>

            <fieldset>
              <legend>
                <p>Services</p>
              </legend>

              <div className="services">
                <div>
                  <button
                    type="button"
                    onClick={() => setService('AppDesign')}
                    className={service === 'AppDesign' ? 'selected' : ''}
                  >
                    AppDesign
                  </button>
                  <button
                    type="button"
                    onClick={() => setService('Site Design')}
                    className={service === 'Site Design' ? 'selected' : ''}
                  >
                    Site Design
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setService('Front-End')}
                    className={service === 'Front-End' ? 'selected' : ''}
                  >
                    Front-End
                  </button>
                  <button
                    type="button"
                    onClick={() => setService('Back-End')}
                    className={service === 'Back-End' ? 'selected' : ''}
                  >
                    Back-End
                  </button>
                  <button
                    type="button"
                    onClick={() => setService('Mobile')}
                    className={service === 'Mobile' ? 'selected' : ''}
                  >
                    Mobile
                  </button>
                </div>
              </div>
            </fieldset>

            <fieldset>
              <div className="form-group">
                <Input name="name" placeholder="Name" required />
                <Input name="email" placeholder="Email" required />
              </div>

              <Input name="company" placeholder="Company's name (optional)" />

              <Textarea
                rows={3}
                name="description"
                placeholder="Project description..."
              />
            </fieldset>

            <button type="submit">send</button>
          </Form>

          <div className="info">
            <h3>
              Before we start talking about the project, I need you to know some
              information:
            </h3>

            <ul>
              <li>
                <FaRegDotCircle size={15} />
                <p>
                  I follow a process, I can adapt, but it is very important that
                  we follow my work model.
                </p>
              </li>
              <li>
                <FaRegDotCircle size={15} />
                <p>I value quality and my focus is interface design.</p>
              </li>
              <li>
                <FaRegDotCircle size={15} />
                <p>
                  Creation is a time-consuming task, so respect the deadlines of
                  the creation process.
                </p>
              </li>
              <li>
                <FaRegDotCircle size={15} />
                <p>
                  I am not an advertising agency, I am not a marketing company,
                  I do not use templates and my projects are all original and
                  made from scratch.
                </p>
              </li>
            </ul>
          </div>
        </Content>
      </Container>
    </Wrapper>
  );
};

export default Contact;
